import "./character.page.css";
import "../../components/character/ActiveItems/active.items.css";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Armor from "../../components/character/ActiveItems/Armor";
import CenteredContainer from "../../components/layout/CenteredContainer/CenteredContainer";
import CharacterInfo from "../../components/character/CharacterInfo";
import CharacterSkills from "../../components/character/Skills/CharacterSkills";
import CombatManeuvers from "../../components/character/CombatManeuvers/CombatManeuvers";
import Characteristics from "../../components/character/Characteristics/Characteristics";
import FormContainer from "../../components/layout/FormContainer/FormContainer";
import Weapon from "../../components/character/ActiveItems/Weapon";
import { fetchGet } from "../../clients/response";
import { SERVICE_URL } from "../../clients/app.const";
import { mapJsonToCharacter, requiresCharacterIdMigration } from "../../models/Character";
import { saveCharacter } from "../../clients/characters.client";
import { useSelector } from "react-redux";
import { useCharacterAutoSave } from "../../hooks/useCharacterAutoSave";
import SaveToast from "../../components/layout/SaveToast/SaveToast";
import { reconcileCharacterRules } from "../../calculations/reconcileCharacterRules";

const CharacterPage = () => {
  const { t } = useTranslation();
  const { tokenValue, user } = useSelector((s) => s.auth);
  const selectedCharacterId = useSelector((s) => s.character.selectedCharacterId);
  const [character, setCharacter] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const updateCharacter = (nextCharacter) => {
    setCharacter((current) => reconcileCharacterRules(
      typeof nextCharacter === "function" ? nextCharacter(current) : nextCharacter
    ));
  };
  const { dismissSaveMessage, isSaveError, retrySave, saveMessage } =
    useCharacterAutoSave(character, tokenValue);

  useEffect(() => {
    const start = async () => {
      setIsLoading(true);
      setError(null);

      if (!selectedCharacterId) {
        setIsLoading(false);
        return;
      }

      const id = selectedCharacterId;
      try {
        const characterData = await fetchGet(
          `${SERVICE_URL}/characters/${id}`,
          {},
          tokenValue
        );

        if (characterData.isError || !characterData.data) {
          setError(t("character.loadError"));
          return;
        }

        const mappedCharacter = mapJsonToCharacter(characterData.data);
        if (requiresCharacterIdMigration(characterData.data)) {
          await saveCharacter(mappedCharacter, tokenValue);
        }
        setCharacter(mappedCharacter);
      } catch (error) {
        console.error("Failed to fetch and process character data:", error);
        setError(t("character.loadError"));
      } finally {
        setIsLoading(false);
      }
    };
    start();
  }, [selectedCharacterId, tokenValue, user, t]);

  return (
    <>
      <CenteredContainer>
        <FormContainer>
          <div className="container">
            {isLoading && <p>{t("character.loading")}</p>}
            {!isLoading && error && <p role="alert">{error}</p>}
            {!isLoading && !error && !character && (
              <p>{t("character.empty")}</p>
            )}
            {character && (
              <>
                <SaveToast
                  isError={isSaveError}
                  message={saveMessage}
                  onDismiss={dismissSaveMessage}
                  onRetry={retrySave}
                />
                <CharacterInfo character={character} onChange={updateCharacter} />
                <Characteristics character={character} onChange={updateCharacter} token={tokenValue} />
                <CharacterSkills character={character} onChange={updateCharacter} />
                <div className="quarter" id="active_items">
                  <div className="items-container">
                    <Armor character={character} onChange={updateCharacter} />
                    <div className="weapon-column">
                      <CombatManeuvers
                        character={character}
                        onChange={updateCharacter}
                      />
                      <Weapon character={character} onChange={updateCharacter} />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </FormContainer>
      </CenteredContainer>
    </>
  );
};

export default CharacterPage;
